import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Download,
  Filter,
  Loader2,
  X,
  CheckCircle2,
  ChefHat,
  PackageCheck,
  Truck,
  Ban,
  Eye,
  Save,
  Trash2,
  ClipboardCopy,
} from "lucide-react";
import { API, useAuth } from "../AuthContext";
import {
  ORDER_STATUSES,
  STATUS_LABEL,
  STATUS_COLOR,
  NEXT_STATUSES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_COLOR,
} from "../constants";
import { exportToCSV, formatDate } from "../utils";

const QUICK_ACTIONS = {
  confirmed: { icon: CheckCircle2, label: "Confirmar" },
  preparing: { icon: ChefHat, label: "Em preparação" },
  ready: { icon: PackageCheck, label: "Pronta" },
  in_route: { icon: Truck, label: "Em rota" },
  delivered: { icon: CheckCircle2, label: "Entregue" },
  cancelled: { icon: Ban, label: "Cancelar" },
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLOR[status] || "bg-gray-100"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function OrdersPage({ presetFilters, onClearPreset }) {
  const { authHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    payment_status: "",
    delivery_from: "",
    delivery_to: "",
    locality: "",
    route_id: "",
    q: "",
  });
  const [detailOrder, setDetailOrder] = useState(null);
  const [slots, setSlots] = useState([]);

  // Apply preset filters from Resumo navigation
  useEffect(() => {
    if (presetFilters) {
      setFilters((prev) => ({
        ...prev,
        status: presetFilters.status || "",
        payment_status: presetFilters.payment_status || "",
        delivery_from: presetFilters.delivery_from || "",
        delivery_to: presetFilters.delivery_to || "",
        route_id: presetFilters.route_id || "",
      }));
    }
  }, [presetFilters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await axios.get(`${API}/orders`, {
        headers: authHeaders(),
        params,
      });
      setOrders(data);
    } catch {
      toast.error("Erro ao carregar encomendas");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axios
      .get(`${API}/admin/delivery-slots?weeks_ahead=6`, { headers: authHeaders() })
      .then((r) => setSlots(r.data.slots))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const localities = useMemo(
    () => Array.from(new Set(orders.map((o) => o.locality).filter(Boolean))).sort(),
    [orders]
  );

  const onStatusChange = async (orderId, newStatus) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${orderId}`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      if (detailOrder?.id === orderId) setDetailOrder(data);
      toast.success(`Estado: ${STATUS_LABEL[newStatus]}`);
    } catch {
      toast.error("Erro ao mudar estado");
    }
  };

  const onSaveOrder = async (orderId, patch) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${orderId}`,
        patch,
        { headers: authHeaders() }
      );
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      setDetailOrder(data);
      toast.success("Encomenda atualizada");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao guardar");
    }
  };

  const onDelete = async (orderId) => {
    if (!window.confirm("Eliminar definitivamente esta encomenda?")) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: authHeaders() });
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setDetailOrder(null);
      toast.success("Encomenda eliminada");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const exportCSV = () => {
    exportToCSV("encomendas-quinta-oliveirinha.csv", orders, [
      { label: "Nº", value: "order_number" },
      { label: "Criada", value: (r) => formatDate(r.created_at) },
      { label: "Data entrega", value: "delivery_date" },
      { label: "Período", value: "delivery_period" },
      { label: "Cliente", value: "name" },
      { label: "Telefone", value: "phone" },
      { label: "Email", value: "email" },
      { label: "Morada", value: "address" },
      { label: "Cód. postal", value: "postal_code" },
      { label: "Localidade", value: "locality" },
      { label: "Itens", value: (r) => (r.items || []).map((i) => `${i.quantity} ${i.unit} ${i.name}`).join(" | ") },
      { label: "Total (€)", value: (r) => Number(r.total).toFixed(2) },
      { label: "Pagamento", value: (r) => PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method },
      { label: "Estado pagamento", value: (r) => PAYMENT_STATUS_LABEL[r.payment_status || "pending"] },
      { label: "Estado", value: (r) => STATUS_LABEL[r.status] || r.status },
      { label: "Rota", value: "route_id" },
      { label: "Obs. cliente", value: "customer_notes" },
      { label: "Notas internas", value: "internal_notes" },
    ]);
  };

  const clearFilters = () => {
    setFilters({ status: "", payment_status: "", delivery_from: "", delivery_to: "", locality: "", route_id: "", q: "" });
    onClearPreset?.();
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid="admin-orders-page" className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Pesquisar</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2C2C2C]/40" />
              <input
                type="text"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Nome, telefone, nº, localidade..."
                data-testid="orders-search"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="orders-filter-status"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            >
              <option value="">Todos</option>
              {ORDER_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Pagamento</label>
            <select
              value={filters.payment_status}
              onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
              data-testid="orders-filter-payment"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            >
              <option value="">Todos</option>
              {PAYMENT_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Entrega de</label>
            <input
              type="date"
              value={filters.delivery_from}
              onChange={(e) => setFilters({ ...filters, delivery_from: e.target.value })}
              data-testid="orders-filter-from"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">até</label>
            <input
              type="date"
              value={filters.delivery_to}
              onChange={(e) => setFilters({ ...filters, delivery_to: e.target.value })}
              data-testid="orders-filter-to"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Localidade</label>
            <select
              value={filters.locality}
              onChange={(e) => setFilters({ ...filters, locality: e.target.value })}
              data-testid="orders-filter-locality"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            >
              <option value="">Todas</option>
              {localities.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Rota</label>
            <select
              value={filters.route_id}
              onChange={(e) => setFilters({ ...filters, route_id: e.target.value })}
              data-testid="orders-filter-route"
              className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            >
              <option value="">Todas</option>
              <option value="none">Sem rota</option>
            </select>
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              data-testid="orders-clear-filters"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#F3F1EB] text-[#2C2C2C]/70 text-xs hover:bg-[#E8DEC8] transition-colors"
            >
              <X size={12} />
              Limpar ({activeFilterCount})
            </button>
          )}

          <button
            onClick={exportCSV}
            disabled={!orders.length}
            data-testid="orders-export-csv"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors disabled:opacity-50"
          >
            <Download size={14} />
            CSV
          </button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#3A4A38]" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-[#D4C4A8]/40 flex items-center gap-2">
            <Filter size={14} className="text-[#3A4A38]" />
            <h3 className="text-sm font-semibold">
              {orders.length} encomenda{orders.length === 1 ? "" : "s"}
            </h3>
          </div>
          {orders.length === 0 ? (
            <p className="p-8 text-center text-[#2C2C2C]/60 text-sm">Sem resultados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead className="bg-[#F3F1EB]/60">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#2C2C2C]/60">
                    <th className="px-3 py-3">Nº</th>
                    <th className="px-3 py-3">Entrega</th>
                    <th className="px-3 py-3">Cliente</th>
                    <th className="px-3 py-3">Contacto</th>
                    <th className="px-3 py-3">Morada</th>
                    <th className="px-3 py-3">Total</th>
                    <th className="px-3 py-3">Pagamento</th>
                    <th className="px-3 py-3">Estado</th>
                    <th className="px-3 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4C4A8]/30">
                  {orders.map((o) => (
                    <tr
                      key={o.id}
                      className={`hover:bg-[#F3F1EB]/40 ${
                        presetFilters?._highlight === o.id ? "bg-amber-50" : ""
                      }`}
                      data-testid={`order-row-${o.id}`}
                    >
                      <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{o.order_number}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        <div className="font-medium">{o.delivery_date}</div>
                        <div className="text-[#2C2C2C]/50">{o.delivery_period}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium">{o.name}</div>
                        <div className="text-xs text-[#2C2C2C]/50">{formatDate(o.created_at)}</div>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <div>{o.phone}</div>
                        <div className="text-[#2C2C2C]/50">{o.email}</div>
                      </td>
                      <td className="px-3 py-3 text-xs max-w-[200px]">
                        <div className="font-medium">{o.locality || "—"}</div>
                        <div className="text-[#2C2C2C]/60 line-clamp-1">{o.address}</div>
                      </td>
                      <td className="px-3 py-3 font-medium whitespace-nowrap">
                        {Number(o.total || 0).toFixed(2)}€
                      </td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        <div>{PAYMENT_METHOD_LABEL[o.payment_method] || o.payment_method}</div>
                        <span
                          className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            PAYMENT_STATUS_COLOR[o.payment_status || "pending"]
                          }`}
                        >
                          {PAYMENT_STATUS_LABEL[o.payment_status || "pending"]}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill status={o.status} />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {(NEXT_STATUSES[o.status] || []).slice(0, 2).map((next) => {
                            const A = QUICK_ACTIONS[next];
                            if (!A) return null;
                            const Icon = A.icon;
                            return (
                              <button
                                key={next}
                                onClick={() => onStatusChange(o.id, next)}
                                title={A.label}
                                data-testid={`order-action-${next}-${o.id}`}
                                className="p-1.5 rounded text-[#3A4A38] hover:bg-[#3A4A38] hover:text-white transition-colors"
                              >
                                <Icon size={14} />
                              </button>
                            );
                          })}
                          <button
                            onClick={() => setDetailOrder(o)}
                            title="Ver detalhes"
                            data-testid={`order-action-detail-${o.id}`}
                            className="p-1.5 rounded text-[#1B93A4] hover:bg-[#1B93A4] hover:text-white transition-colors"
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          slots={slots}
          onClose={() => setDetailOrder(null)}
          onSave={onSaveOrder}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

// ============ DETAIL MODAL ============

function OrderDetailModal({ order, slots, onClose, onSave, onStatusChange, onDelete }) {
  const [edit, setEdit] = useState({
    address: order.address,
    postal_code: order.postal_code || "",
    locality: order.locality,
    phone: order.phone,
    customer_notes: order.customer_notes || "",
    internal_notes: order.internal_notes || "",
    delivery_date: order.delivery_date,
    delivery_period: order.delivery_period,
    payment_method: order.payment_method,
    payment_status: order.payment_status || "pending",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEdit({
      address: order.address,
      postal_code: order.postal_code || "",
      locality: order.locality,
      phone: order.phone,
      customer_notes: order.customer_notes || "",
      internal_notes: order.internal_notes || "",
      delivery_date: order.delivery_date,
      delivery_period: order.delivery_period,
      payment_method: order.payment_method,
      payment_status: order.payment_status || "pending",
    });
  }, [order]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(edit).forEach(([k, v]) => {
        if (v !== (order[k] ?? "")) patch[k] = v;
      });
      if (Object.keys(patch).length === 0) {
        toast.info("Sem alterações.");
        return;
      }
      await onSave(order.id, patch);
    } finally {
      setSaving(false);
    }
  };

  const copyContact = () => {
    const text = `${order.name}\n${order.phone}\n${order.address}, ${order.postal_code || ""} ${order.locality}`;
    navigator.clipboard.writeText(text);
    toast.success("Contacto copiado");
  };

  const onPrint = () => window.print();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="order-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-[#D4C4A8]/40 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Encomenda <span className="font-mono">{order.order_number}</span></h2>
              <StatusPill status={order.status} />
            </div>
            <p className="text-xs text-[#2C2C2C]/60 mt-0.5">Criada {formatDate(order.created_at)}</p>
          </div>
          <button onClick={onClose} data-testid="order-detail-close" className="p-1 rounded hover:bg-[#F3F1EB]">
            <X size={20} />
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5 space-y-6">
          {/* Quick status actions */}
          <div className="flex flex-wrap gap-2">
            {(NEXT_STATUSES[order.status] || []).map((next) => {
              const A = QUICK_ACTIONS[next];
              if (!A) return null;
              const Icon = A.icon;
              return (
                <button
                  key={next}
                  onClick={() => onStatusChange(order.id, next)}
                  data-testid={`detail-action-${next}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#3A4A38] text-white text-xs hover:bg-[#2C3A2A] transition-colors"
                >
                  <Icon size={14} />
                  {A.label}
                </button>
              );
            })}
            <button
              onClick={copyContact}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F3F1EB] text-[#2C2C2C] text-xs hover:bg-[#E8DEC8] transition-colors"
            >
              <ClipboardCopy size={14} />
              Copiar contacto
            </button>
            <button
              onClick={onPrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F3F1EB] text-[#2C2C2C] text-xs hover:bg-[#E8DEC8] transition-colors"
            >
              Imprimir
            </button>
          </div>

          {/* Customer */}
          <section>
            <h3 className="text-sm font-semibold text-[#2C2C2C] mb-3">Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Nome</label>
                <input value={order.name} readOnly className="w-full px-3 py-2 rounded border border-[#D4C4A8]/60 bg-[#F3F1EB]/30 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Email</label>
                <input value={order.email} readOnly className="w-full px-3 py-2 rounded border border-[#D4C4A8]/60 bg-[#F3F1EB]/30 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Telefone</label>
                <input
                  value={edit.phone}
                  onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                  data-testid="detail-phone"
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                />
              </div>
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Método de pagamento</label>
                <select
                  value={edit.payment_method}
                  onChange={(e) => setEdit({ ...edit, payment_method: e.target.value })}
                  data-testid="detail-payment-method"
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                >
                  {PAYMENT_METHODS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <h3 className="text-sm font-semibold text-[#2C2C2C] mb-3">Morada de entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Rua</label>
                <input
                  value={edit.address}
                  onChange={(e) => setEdit({ ...edit, address: e.target.value })}
                  data-testid="detail-address"
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                />
              </div>
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Código postal</label>
                <input
                  value={edit.postal_code}
                  onChange={(e) => setEdit({ ...edit, postal_code: e.target.value })}
                  placeholder="0000-000"
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Localidade</label>
                <input
                  value={edit.locality}
                  onChange={(e) => setEdit({ ...edit, locality: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                />
              </div>
            </div>
          </section>

          {/* Delivery date */}
          <section>
            <h3 className="text-sm font-semibold text-[#2C2C2C] mb-3">Entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Data prevista (Qua/Qui/Sex)</label>
                <select
                  value={edit.delivery_date}
                  onChange={(e) => setEdit({ ...edit, delivery_date: e.target.value })}
                  data-testid="detail-delivery-date"
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                >
                  {/* Always include current value even if outside the slot list */}
                  {!slots.some((s) => s.date === edit.delivery_date) && edit.delivery_date && (
                    <option value={edit.delivery_date}>{edit.delivery_date}</option>
                  )}
                  {slots.map((s) => (
                    <option key={s.date} value={s.date}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#2C2C2C]/60 mb-1">Período</label>
                <select
                  value={edit.delivery_period}
                  onChange={(e) => setEdit({ ...edit, delivery_period: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
                >
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </select>
              </div>
            </div>
          </section>

          {/* Payment status quick actions */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#2C2C2C]">Estado do pagamento</h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  PAYMENT_STATUS_COLOR[edit.payment_status]
                }`}
                data-testid="detail-payment-status-pill"
              >
                {PAYMENT_STATUS_LABEL[edit.payment_status]}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_STATUSES.map((s) => (
                <button
                  key={s.key}
                  onClick={async () => {
                    setEdit({ ...edit, payment_status: s.key });
                    await onSave(order.id, { payment_status: s.key });
                  }}
                  data-testid={`detail-payment-${s.key}`}
                  disabled={edit.payment_status === s.key}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    edit.payment_status === s.key
                      ? "bg-[#3A4A38] text-white cursor-default"
                      : "bg-white border border-[#D4C4A8] text-[#2C2C2C] hover:bg-[#F3F1EB]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#2C2C2C]/50 mt-2">
              Pagamento manual — marque como <strong>Pago</strong> quando confirmar a receção (MB Way, transferência ou dinheiro).
            </p>
          </section>

          {/* Items */}
          <section>
            <h3 className="text-sm font-semibold text-[#2C2C2C] mb-3">Produtos encomendados</h3>
            {(order.items || []).length === 0 ? (
              <p className="text-xs text-[#2C2C2C]/50 italic">Sem itens individuais (encomenda manual).</p>
            ) : (
              <table className="w-full text-sm border border-[#D4C4A8]/40 rounded overflow-hidden">
                <thead className="bg-[#F3F1EB]/60">
                  <tr className="text-xs text-[#2C2C2C]/70">
                    <th className="text-left px-3 py-2">Produto</th>
                    <th className="text-right px-3 py-2">Qtd</th>
                    <th className="text-left px-3 py-2">Unidade</th>
                    <th className="text-right px-3 py-2">Preço un.</th>
                    <th className="text-right px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4C4A8]/30">
                  {order.items.map((it, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2">{it.name}</td>
                      <td className="text-right px-3 py-2">{it.quantity}</td>
                      <td className="px-3 py-2">{it.unit}</td>
                      <td className="text-right px-3 py-2">{Number(it.unit_price).toFixed(2)}€</td>
                      <td className="text-right px-3 py-2 font-medium">{Number(it.total).toFixed(2)}€</td>
                    </tr>
                  ))}
                  <tr className="bg-[#F3F1EB]/40 font-semibold">
                    <td colSpan={4} className="text-right px-3 py-2">Total</td>
                    <td className="text-right px-3 py-2">{Number(order.total || 0).toFixed(2)}€</td>
                  </tr>
                </tbody>
              </table>
            )}
          </section>

          {/* Notes */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#2C2C2C]/60 mb-1">Observações do cliente</label>
              <textarea
                value={edit.customer_notes}
                onChange={(e) => setEdit({ ...edit, customer_notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
            <div>
              <label className="block text-xs text-[#2C2C2C]/60 mb-1">Notas internas (só admin)</label>
              <textarea
                value={edit.internal_notes}
                onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })}
                data-testid="detail-internal-notes"
                rows={3}
                className="w-full px-3 py-2 rounded border border-amber-300 bg-amber-50/40 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </section>
        </div>

        <footer className="px-6 py-4 border-t border-[#D4C4A8]/40 flex items-center justify-between gap-3 bg-[#F3F1EB]/40">
          <button
            onClick={() => onDelete(order.id)}
            data-testid="detail-delete-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-red-600 hover:bg-red-50 text-xs transition-colors"
          >
            <Trash2 size={14} />
            Eliminar
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm text-[#2C2C2C]/70 hover:bg-[#F3F1EB] transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="detail-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar alterações
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
